<script setup>
import { computed } from 'vue'
import { useStore } from 'vuex'
import moment from 'moment'

import { date } from '@/filtersNew'
import PlayoutScheduleTableItem from './PlayoutScheduleTableItem.vue'

const store = useStore()

const props = defineProps({
	mediaPulse: {
		type: Object,
		default: null,
	},
})

const playoutConfig = computed(() => store.getters['Playout/playoutConfig'])
const playlist = computed(() => store.getters['Playout/playlist'] || [])

const isScheduleMode = computed(() => playoutConfig.value?.mode === 'schedule')

// Shared column template + min width so the header and every row stay aligned.
// The min width keeps columns readable on mobile — the table scrolls sideways
// instead of cramming/wrapping.
const gridCols = computed(() =>
	isScheduleMode.value
		? 'grid-cols-[48px_minmax(160px,1fr)_128px_180px_120px_44px]'
		: 'grid-cols-[48px_minmax(160px,1fr)_128px_120px_44px]',
)
// Min width must cover the fixed track widths + column gaps (gap-x-3 = 12px) +
// horizontal padding (px-3 = 24px), otherwise the right-hand columns overflow the
// row box and lose the shared background / divider lines.
// schedule: 680 tracks + 5×12 gap + 24 pad = 764 → 768
// loop:     500 tracks + 4×12 gap + 24 pad = 572 → 576
const minWidthClass = computed(() => (isScheduleMode.value ? 'min-w-[768px]' : 'min-w-[576px]'))

const shouldShowDay = (index) => {
	const currentItemDay = moment(playlist.value[index]?.startTime)
	const beforePlayoutItem = moment(playlist.value[index - 1]?.startTime)
	if (index === 0 || !currentItemDay.isSame(beforePlayoutItem, 'day')) {
				return 'before:block before:px-3 before:pt-2 before:content-[attr(data-day)] before:text-surface-6 before:text-xs before:font-medium'
	}
	return ''
}
const itemDay = (index) => {
	const playoutItem = playlist.value[index]
	if (!playoutItem) return ''
	return date(playoutItem.startTime, 'dddd DD, MMMM YYYY', true)
}

const handleDragStart = (e) => {
	e.dataTransfer.setData('text/plain', e.currentTarget.dataset.index)
}
const handleDragEnter = (e) => {
	e.currentTarget.classList.add('hover')
}
const handleDragLeave = (e) => {
	e.currentTarget.classList.remove('hover')
}
const handleDragOver = (e) => {
	e.preventDefault()
}
const handleDrop = (e) => {
	e.preventDefault()
	const itemIndex = e.dataTransfer.getData('text/plain')
	const droppedIndex = e.target.closest('li')?.dataset.index
	if (droppedIndex === undefined) return
	Array.from(e.currentTarget.parentNode.children).forEach((tr) => {
		tr.classList.remove('hover')
	})
	const oldIndex = parseInt(itemIndex, 10)
	const newIndex = parseInt(droppedIndex, 10)
	store.dispatch('Playout/setNewOrder', { oldIndex, newIndex })
}
</script>

<template>
	<div class="rounded-lg overflow-hidden bg-surface-2">
		<!-- Horizontal scroll on small screens: the table keeps its columns and
		     scrolls sideways instead of wrapping. -->
		<div class="overflow-x-auto">
			<div :class="minWidthClass">
				<div
					class="grid gap-x-3 bg-surface-3 text-tiny text-surface-8 px-3 h-10 items-center font-medium"
					:class="gridCols"
				>
					<div>#</div>
					<div>Source</div>
					<div>Type</div>
					<div v-if="isScheduleMode">Start time</div>
					<div class="text-right">Duration</div>
					<div></div>
				</div>
				<ul class="divide-y divide-surface-3 m-0 p-0 list-none">
					<li
						v-for="(playoutItem, index) in playlist"
						:key="playoutItem._uid || playoutItem.index || index"
						:data-index="index"
						:data-day="itemDay(index)"
						draggable="true"
						:class="shouldShowDay(index)"
						@dragstart="handleDragStart"
						@dragenter="handleDragEnter"
						@dragleave="handleDragLeave"
						@dragover="handleDragOver"
						@drop="handleDrop"
					>
						<PlayoutScheduleTableItem :item="playoutItem" :index="index" :media-pulse="mediaPulse" />
					</li>
				</ul>
			</div>
		</div>
	</div>
</template>

<style scoped>
li[draggable='true'] {
	cursor: move;
}
li[draggable='true'].hover {
	opacity: 0.4;
}
</style>
